import { JSEncrypt } from "jsencrypt";
import keyManager from "./keyManager";

/**
 * EncryptionService - خدمة التشفير
 * تتعامل مع تشفير وفك تشفير البيانات
 */
class EncryptionService {
  constructor() {
    this.keyManager = keyManager;
  }

  /**
   * توليد Nonce عشوائي
   * @param {number} length - طول الـ Nonce (افتراضي 12 بايت)
   * @returns {Uint8Array} Nonce
   */
  generateNonce(length = 12) {
    const nonce = new Uint8Array(length);
    crypto.getRandomValues(nonce);
    return nonce;
  }

  /**
   * تحويل Uint8Array إلى Base64
   * @param {Uint8Array} uint8Array
   * @returns {string} Base64 string
   */
  uint8ArrayToBase64(uint8Array) {
    return btoa(String.fromCharCode(...uint8Array));
  }

  /**
   * تحويل Base64 إلى Uint8Array
   * @param {string} base64
   * @returns {Uint8Array}
   */
  base64ToUint8Array(base64) {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
  }

  /**
   * تشفير البيانات باستخدام AES-256-GCM
   * @param {string|ArrayBuffer} data - البيانات للتشفير
   * @param {string} aesKey - مفتاح AES كنص (يُحوّل إلى bytes UTF-8)
   * @returns {Promise<{encryptedData: string, nonce: string}>}
   */
  async encryptAes(data, aesKey) {
    try {
      // توليد Nonce (12 بايت)
      const nonce = this.generateNonce(12);

      // تحويل البيانات إلى ArrayBuffer
      const dataBuffer =
        typeof data === "string"
          ? new TextEncoder().encode(data).buffer
          : data;

      // تحويل AES key (string) إلى bytes ثم إلى CryptoKey
      const aesKeyBytes = new TextEncoder().encode(aesKey);
      const cryptoKey = await crypto.subtle.importKey(
        "raw",
        aesKeyBytes,
        { name: "AES-GCM" },
        false,
        ["encrypt"]
      );

      // تشفير البيانات
      const encrypted = await crypto.subtle.encrypt(
        {
          name: "AES-GCM",
          iv: nonce,
          tagLength: 128, // 128 bits = 16 bytes
        },
        cryptoKey,
        dataBuffer
      );

      // النتيجة: encrypted data + tag (16 bytes في النهاية)
      // تحويل إلى Base64
      const encryptedBase64 = this.uint8ArrayToBase64(
        new Uint8Array(encrypted)
      );
      const nonceBase64 = this.uint8ArrayToBase64(nonce);

      return {
        encryptedData: encryptedBase64,
        nonce: nonceBase64,
      };
    } catch (error) {
      throw error;
    }
  }

  /**
   * فك تشفير البيانات باستخدام AES-256-GCM
   * @param {string} encryptedDataBase64 - البيانات المشفرة (Base64)
   * @param {string} nonceBase64 - Nonce (Base64)
   * @param {string} aesKey - مفتاح AES كنص (يُحوّل إلى bytes UTF-8)
   * @returns {Promise<Uint8Array>} البيانات المفكوكة
   */
  async decryptAes(encryptedDataBase64, nonceBase64, aesKey) {
    try {
      // تحويل من Base64
      const encryptedData = this.base64ToUint8Array(encryptedDataBase64);
      const nonce = this.base64ToUint8Array(nonceBase64);

      // تحويل AES key (string) إلى bytes ثم إلى CryptoKey
      const aesKeyBytes = new TextEncoder().encode(aesKey);
      const cryptoKey = await crypto.subtle.importKey(
        "raw",
        aesKeyBytes,
        { name: "AES-GCM" },
        false,
        ["decrypt"]
      );

      // فك التشفير
      const decrypted = await crypto.subtle.decrypt(
        {
          name: "AES-GCM",
          iv: nonce,
          tagLength: 128,
        },
        cryptoKey,
        encryptedData
      );

      return new Uint8Array(decrypted);
    } catch (error) {
      throw error;
    }
  }

  /**
   * تشفير مفتاح AES باستخدام RSA
   * @param {string} aesKey - مفتاح AES كنص (UTF-8)
   * @param {string} publicKeyPem - المفتاح العام RSA (PEM format)
   * @returns {Promise<string>} مفتاح AES مشفر (Base64)
   */
  async encryptAesKeyWithRsa(aesKey, publicKeyPem) {
    try {
      // استخدام JSEncrypt
      const encrypt = new JSEncrypt();
      encrypt.setPublicKey(publicKeyPem);

      // تشفير باستخدام RSA (PKCS#1 v1.5) لنص المفتاح مباشرة
      // السيرفر يفك RSA ثم يستخدم الناتج كنص ويحوّله لـ UTF8 bytes كمفتاح AES-GCM
      const encrypted = encrypt.encrypt(aesKey);

      if (!encrypted) {
        throw new Error("RSA encryption failed");
      }

      return encrypted;
    } catch (error) {
      throw error;
    }
  }

  /**
   * الحصول على مفتاح AES مشفر (لإرساله في Header)
   * @returns {Promise<string>} مفتاح AES مشفر بـ RSA
   */
  async getEncryptedAesKey() {
    const aesKey = await this.keyManager.getAesKey(); // string
    const publicKey = await this.keyManager.getPublicKey();

    if (!publicKey) {
      throw new Error("RSA public key not available");
    }

    return await this.encryptAesKeyWithRsa(aesKey, publicKey);
  }

  /**
   * تشفير body الطلب
   * @param {Object} requestBody - Body الطلب
   * @returns {Promise<{encrypt: string}>} البيانات المشفرة
   */
  async encryptRequest(requestBody) {
    try {
      // الحصول على مفتاح AES
      const aesKey = await this.keyManager.getAesKey();

      // تحويل body إلى JSON ثم إلى bytes
      const jsonString = JSON.stringify(requestBody);
      const jsonBytes = new TextEncoder().encode(jsonString);

      // تشفير باستخدام AES
      const encrypted = await this.encryptAes(jsonBytes, aesKey);

      // دمج البيانات المشفرة والـ Nonce بنقطة
      // الصيغة: Base64(Data+Tag).Base64(Nonce)
      const combinedEncrypted = `${encrypted.encryptedData}.${encrypted.nonce}`;

      return { encrypt: combinedEncrypted };
    } catch (error) {
      throw error;
    }
  }

  /**
   * فك تشفير response
   * @param {Object} encryptedResponse - الرد المشفر
   * @returns {Promise<Object>} البيانات المفكوكة
   */
  async decryptResponse(encryptedResponse) {
    try {
      // استخراج البيانات المشفرة من حقل 'data'
      const dataString = encryptedResponse.data;
      if (!dataString) {
        throw new Error("Encrypted data not found in response");
      }

      // فصل البيانات المشفرة والـ Nonce (مفصولة بنقطة)
      const lastDotIndex = dataString.lastIndexOf(".");
      if (lastDotIndex <= 0 || lastDotIndex >= dataString.length - 1) {
        throw new Error("Invalid encrypted data format");
      }

      const encryptedDataBase64 = dataString.substring(0, lastDotIndex);
      const nonceBase64 = dataString.substring(lastDotIndex + 1);

      // الحصول على مفتاح AES
      const aesKey = await this.keyManager.getAesKey();

      // فك التشفير
      const decryptedBytes = await this.decryptAes(
        encryptedDataBase64,
        nonceBase64,
        aesKey
      );

      // تحويل إلى JSON
      const jsonString = new TextDecoder().decode(decryptedBytes);
      const decryptedJson = JSON.parse(jsonString);

      return decryptedJson;
    } catch (error) {
      throw error;
    }
  }
}

// Export singleton instance
export const encryptionService = new EncryptionService();
export default encryptionService;
